import React, { useEffect, useState } from 'react';
import Swal, { SweetAlertOptions } from 'sweetalert2';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { UseOutletContextService } from './Service';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { ServiceDA } from '../../../../services/service.service';
import { Error } from '../../../../models/error.model';
import { Message } from '../../../../models/message.model';
import { Service } from '../../../../models/service.model';
import { uploadTooltip } from '../../../../tools/tooltip.tool';
import { getError, getMessage } from '../../../../languages/translations/response';

import AppPreloader from '../../../../components/Preloader';
import AppInventoryViewDisplay from '../component/InventoryViewDisplay';
import AppInventoryViewNames from '../component/InventoryViewNames';
import AppInventoryViewAccountingAccounts from '../component/InventoryViewAccountingAccount';
import AppInventoryViewDetail from '../component/InventoryViewDetail';
import AppInventoryViewTags from '../component/InventoryViewTags';
import AppInventoryViewDate from '../component/InventoryViewDate';

export interface ServiceViewPageProps {};

let errorResponse: Error, messageResponse: Message, serviceResponse: Service;

const ServiceViewPage: React.FunctionComponent<ServiceViewPageProps> = props => {
  const {setRoute, companyForUser, accountingExpenseAccountsForCompany, accountingVatAccountsForCompany, accountingProductAccountsForCompany, loadServicesForCompany} = UseOutletContextService()
  const {lang} = StateLanguage()
  const param = useParams()
  const navigate = useNavigate()

  const [mounted, setMounted] = useState(false)
  const [service, setService] = useState<Service | null>(null)

  const loadService = async (id_company: string, id_service: string) => {
    await ServiceDA.getService(id_company, id_service).then( (response) => {
      if (response.status === 200) {
        serviceResponse = response.data
        setService(serviceResponse)
      } else {
        errorResponse = response.data

        Swal.fire({
          title: getError(errorResponse.code, lang.code),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          buttonsStyling: !1,
          confirmButtonText: lang.labels.okGotIt,
          customClass: {confirmButton: 'btn btn-primary'}
        } as SweetAlertOptions)
      }
    }).catch( (error) => {
      console.error(error)
      navigate('/error')
    })
  }

  const submitServiceDelete = () => {
    if (companyForUser && service) {
      Swal.fire({
        title: lang.labels.youWantDeleteService,
        text: lang.labels.youWillNotBeAbleToUndo,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: lang.labels.yesDelete,
        cancelButtonText: lang.labels.noCancel,
        customClass: {confirmButton:'btn btn-primary', cancelButton:'btn btn-secondary'}
      }).then(async (result) => {
        if (result.isConfirmed) {
          await ServiceDA.deleteServices(companyForUser.id, [service.id]).then( (response) => {
            if (response.status === 200) {
              messageResponse = response.data
              loadServicesForCompany(companyForUser.id)

              Swal.fire({
                title: getMessage(messageResponse.message, lang.code),
                text: lang.labels.actionCompletedReturningToPage,
                icon: 'success',
                showConfirmButton: false,
                timer: 1800
              } as SweetAlertOptions).then( () => {
                navigate('/app/inventory/service/list', {replace: true})
              })
            } else {
              errorResponse = response.data

              Swal.fire({
                title: getError(errorResponse.code, lang.code),
                text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
                icon: 'error',
                buttonsStyling: !1,
                confirmButtonText: lang.labels.okGotIt,
                customClass: {confirmButton: 'btn btn-primary'}
              } as SweetAlertOptions)
            }
          }).catch( (error) => {
            console.error(error)
            navigate('/error')
          })
        }
      })
    }
  }

  useEffect( () => {
    if (companyForUser && param.id) {
      loadService(companyForUser.id, param.id)
      uploadTooltip()
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, param])

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.services, branch: lang.labels.viewService}, company: false})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { companyForUser && accountingExpenseAccountsForCompany && accountingVatAccountsForCompany && accountingProductAccountsForCompany && service
        ?
        <div className="d-flex flex-column flex-xl-row">
          <div className="d-flex flex-column flex-xl-row-auto gap-5 w-100 w-xl-350px mb-5 mb-xl-0">
            <div className="d-xl-none d-flex flex-wrap justify-content-end gap-2">
              <Link to={"/app/inventory/service/update/" + service.id} className="btn btn-primary btn-sm">{lang.labels.editService}</Link>
              <button className="btn btn-danger btn-sm" type="button" onClick={submitServiceDelete}>{lang.labels.deleteService}</button>
            </div>
            <AppInventoryViewDisplay inventory={"service"} record={service}></AppInventoryViewDisplay>
            <AppInventoryViewNames record={service}></AppInventoryViewNames>
            <AppInventoryViewAccountingAccounts display={"d-none d-xl-block"} record={service}></AppInventoryViewAccountingAccounts>
          </div>
          <div className="d-flex flex-column flex-row-fluid gap-5 ms-xl-5">
            <div className="d-none d-xl-flex flex-wrap justify-content-end gap-3">
              <Link to={"/app/inventory/service/update/" + service.id} className="btn btn-primary btn-sm">{lang.labels.editService}</Link>
              <button className="btn btn-danger btn-sm" type="button" onClick={submitServiceDelete}>{lang.labels.deleteService}</button>
            </div>
            <div className="card card-flush">
              <div className="card-header">
                <div className="card-title">
                  <h5>{lang.labels.details}</h5>
                </div>
              </div>
              <div className="card-body pt-0">
                <AppInventoryViewDetail inventory={"service"} record={service}></AppInventoryViewDetail>
              </div>
            </div>
            <AppInventoryViewTags record={service}></AppInventoryViewTags>
            <AppInventoryViewAccountingAccounts display={"d-block d-xl-none"} record={service}></AppInventoryViewAccountingAccounts>
            <AppInventoryViewDate record={service}></AppInventoryViewDate>
          </div>
        </div>
        :
        <AppPreloader></AppPreloader>
      }
    </div>
  )
};

export default ServiceViewPage;
